<template>
  <BaseDialog ref="dialog" contentClass="payment-link" :show-actions="true" @closeModal="closeDialog">
    <template slot="title">{{ $t('page.admin-subscriptions.link-generator.title') }}</template>

    <template slot="content">
      <p class="payment-link-dialog__intro">
        {{ $t('page.admin-subscriptions.link-generator.intro', { name: tenant ? tenant.tenantName : '' }) }}
      </p>

      <div class="payment-link-dialog__group">
        <span class="payment-link-dialog__group-label">
          {{ $t('page.admin-subscriptions.link-generator.plan') }}
        </span>
        <div class="payment-link-dialog__chips">
          <!-- Entreprise is absent on purpose: it has no Payment Link and is invoiced from QuickBooks. -->
          <button
            v-for="plan in plans"
            :key="plan"
            type="button"
            class="payment-link-dialog__chip"
            :class="{ 'is-selected': planCode === plan }"
            :aria-pressed="String(planCode === plan)"
            @click="selectPlan(plan)"
          >
            {{ $t(`subscriptions.${plan}`) }}
          </button>
        </div>
      </div>

      <div class="payment-link-dialog__group">
        <span class="payment-link-dialog__group-label">
          {{ $t('page.admin-subscriptions.link-generator.interval') }}
        </span>
        <div class="payment-link-dialog__chips">
          <button
            v-for="option in intervals"
            :key="option"
            type="button"
            class="payment-link-dialog__chip"
            :class="{ 'is-selected': interval === option }"
            :aria-pressed="String(interval === option)"
            @click="selectInterval(option)"
          >
            {{ $t(`page.admin-subscriptions.interval.${option}`) }}
          </button>
        </div>
      </div>

      <div class="payment-link-dialog__group payment-link-dialog__group--inline">
        <span class="payment-link-dialog__group-label">
          {{ $t('page.admin-subscriptions.link-generator.with-bi') }}
        </span>
        <BaseToggle :value="withBi" toggle-key="payment-link-bi" @change="onBiChange" />
      </div>

      <div v-if="url" class="payment-link-dialog__result">
        <code class="payment-link-dialog__url">{{ url }}</code>
        <BaseButton isSmall isSquare has-icon-left @click.native="copy">
          <BaseIcon name="copy" />
          {{ copied ? $t('page.admin-subscriptions.link-generator.copied') : $t('actions.copy') }}
        </BaseButton>
      </div>

      <p v-if="error" class="payment-link-dialog__error">{{ error }}</p>
    </template>

    <template slot="actions">
      <BaseButton isOutlined @click.native="closeDialog">{{ $t('actions.close') }}</BaseButton>
      <BaseButton :disabled="!planCode || loading" @click.native="generate">
        {{ $t('page.admin-subscriptions.link-generator.generate') }}
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseToggle from '@/components/base/BaseToggle';
import { billingService } from '@/services/billing.service';
import { copyTextToClipboard } from '@/util/clipboard';

/**
 * Produces a Payment Link URL with `client_reference_id` already on it.
 *
 * Exists because a human remembering to append a tenant UUID is not a mechanism. A link sent without
 * one produces a Stripe customer with no way back to Aloha, and the mistake stays invisible until
 * somebody notices an unlinked customer days later.
 *
 * Not a checkout builder: the backend holds the twelve configured URLs and this only picks which one.
 * When no link is configured for a combination the backend 404s, and that is shown rather than
 * swallowed — producing a bare link would recreate the exact problem this dialog prevents.
 */
export default {
  name: 'PaymentLinkDialog',
  components: { BaseButton, BaseDialog, BaseIcon, BaseToggle },
  data() {
    return {
      tenant: null,
      plans: ['essential', 'plus', 'pro'],
      intervals: ['month', 'year'],
      planCode: null,
      interval: 'month',
      withBi: false,
      url: null,
      error: null,
      loading: false,
      copied: false,
    };
  },
  methods: {
    openDialog(tenant) {
      this.tenant = tenant;
      this.clear();
      // Start from what the tenant already has, when it is one of the sellable plans. The usual
      // reason to generate a link for an existing subscriber is a renewal or a lost payment method,
      // not a plan change. An untagged legacy price or no subscription starts blank, as before.
      if (this.plans.includes(tenant.planCode)) {
        this.planCode = tenant.planCode;
        if (this.intervals.includes(tenant.planInterval)) this.interval = tenant.planInterval;
      }
      if (tenant.biItemBilled) this.withBi = true;
      this.$refs.dialog.openModal();
    },

    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.tenant = null;
      this.clear();
    },

    clear() {
      this.planCode = null;
      this.interval = 'month';
      this.withBi = false;
      this.url = null;
      this.error = null;
      this.loading = false;
      this.copied = false;
    },

    // Any change to the combination invalidates a generated URL — showing a stale link next to
    // changed options is how the wrong link gets copied.
    selectPlan(plan) {
      this.planCode = plan;
      this.url = null;
      this.error = null;
      this.copied = false;
    },

    selectInterval(interval) {
      this.interval = interval;
      this.url = null;
      this.error = null;
      this.copied = false;
    },

    onBiChange(value) {
      this.withBi = value;
      this.url = null;
      this.error = null;
      this.copied = false;
    },

    generate() {
      if (!this.tenant || !this.planCode || this.loading) return Promise.resolve();

      this.loading = true;
      this.error = null;
      this.url = null;
      this.copied = false;

      return billingService
        .getPaymentLink({
          tenantId: this.tenant.tenantId,
          planCode: this.planCode,
          interval: this.interval,
          bi: this.withBi,
        })
        .then((url) => {
          this.url = url;
        })
        .catch((e) => {
          // 404 means no link is configured for that plan/interval/BI combination — a phase 0 gap,
          // not a user error. Say which, so somebody fixes the config instead of retrying.
          this.error =
            e && e.response && e.response.status === 404
              ? this.$t('page.admin-subscriptions.link-generator.not-configured')
              : this.$t('page.admin-subscriptions.link-generator.failed');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    copy() {
      if (!this.url) return;
      copyTextToClipboard(
        this.url,
        () => {
          this.copied = true;
        },
        () => this.$toast.error(this.$t('page.analysis.text-selection.copy.error')),
      );
    },
  },
};
</script>

<style lang="scss">
.base-dialog__payment-link {
  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .base-dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 32px;
  }
}

.payment-link-dialog {
  &__intro {
    margin-bottom: 20px;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;

    &--inline {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }
  }

  &__group-label {
    font-size: 13px;
    font-weight: 600;
    color: $color-normal;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    padding: 6px 14px;
    background: $color-white;
    border: 1px solid $color-neutral-pale;
    border-radius: 999px;
    cursor: pointer;
    font-size: 13px;

    &.is-selected {
      background: $color-normal;
      color: $color-white;
      border-color: $color-normal;
    }
  }

  &__result {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 20px;
    padding: 12px;
    background: $color-neutral-pale;
    border-radius: $border-radius-root;
  }

  &__url {
    flex: 1;
    font-family: monospace;
    font-size: 12px;
    word-break: break-all;
  }

  &__error {
    margin-top: 16px;
    font-size: 13px;
    color: #9b2a2a;
  }
}
</style>
